import React, { useState, useEffect } from 'react';
import { Container, Form, Card, Button, Alert } from 'react-bootstrap';
import { settingsAPI } from '../api/settingsAPI';
import { vendorAPI } from '../api/vendorAPI';
import { usePermissions } from '../contexts/PermissionContext';

function SystemSettingsScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const [settings, setSettings] = useState({
        enable_warehouse_transit: true,
        company_name: '',
        default_manufacturer: ''
    });
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        loadSettings();
        loadVendors();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsAPI.get();
            setSettings(data);
        } catch (error) {
            setAlert({ variant: 'danger', message: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const loadVendors = async () => {
        try {
            const data = await vendorAPI.getAll();
            setVendors(data);
        } catch (error) {
            console.error('Failed to load vendors:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await settingsAPI.update(settings);
            setAlert({ variant: 'success', message: 'Settings updated successfully' });
        } catch (error) {
            setAlert({ variant: 'danger', message: 'Failed to update settings' });
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value || null
        }));
    };

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    if (!hasPermission('system.settings.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view system settings.
                </Alert>
            </Container>
        )
    }

    const canModify = hasPermission('system.settings.modify');

    return (
        <Container className="py-3">
            <h1 className="mb-4">System Settings</h1>
            {alert && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                    dismissible
                >
                    {alert.message}
                </Alert>
            )}
            {!canModify && (
                <Alert variant="info">
                    You can view settings but don&apos;t have permission to modify them.
                </Alert>
            )}
            <Form onSubmit={handleSubmit}>
                <Card className="mb-4">
                    <Card.Header>Company Information</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Company Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="company_name"
                                value={settings.company_name || ''}
                                onChange={handleInputChange}
                                disabled={!canModify}
                                placeholder="Enter company name"
                            />
                            <Form.Text className="text-muted">
                                Company name for general use (reports, labels, etc.)
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Default Manufacturer</Form.Label>
                            <Form.Select
                                name="default_manufacturer"
                                value={settings.default_manufacturer || ''}
                                onChange={handleInputChange}
                                disabled={!canModify}
                            >
                                <option value="">-- Select Vendor --</option>
                                {vendors.map((vendor) => (
                                    <option key={vendor._id} value={vendor._id}>
                                        {vendor.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Default vendor to use as manufacturer for in-house manufactured products
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4">
                    <Card.Header>Warehouse Settings</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="enable_warehouse_transit"
                                name="enable_warehouse_transit"
                                label="Enable Warehouse Transit"
                                checked={settings.enable_warehouse_transit}
                                onChange={handleCheckboxChange}
                                disabled={!canModify}
                            />
                            <Form.Text className="text-muted">
                                When enabled, items must go through transit when moving between warehouses.
                                When disabled, items can be moved directly between warehouses.
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {canModify && (
                    <Button type="submit" variant="primary">
                        Save Changes
                    </Button>
                )}
            </Form>
        </Container>
    );
}

export default SystemSettingsScreen; 